import { Component, signal, computed, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Title, Meta } from '@angular/platform-browser';
import { MainLayout } from '../../layouts/main-layout';
import {
  marcasProductos,
  type Brand,
} from '../../utils/mockups/marcas-productos';
import { MarginWrapper } from '../../layouts/margin-wrapper';
import { toSlug } from '../../utils/slugs/slug.utils';

@Component({
  selector: 'allas-comprar-por-marca-producto',
  imports: [MainLayout, MarginWrapper, CommonModule],
  template: `
    <allas-main-layout [showPopularSections]="false" [showNewsletter]="false">
      <allas-margin-wrapper styleClass="fs-condensed">
        <div class="py-8">
          <!-- Header -->
          <div class="mb-8">
            <h1 class="text-4xl font-bold text-gray-800 mb-4">
              Comprar por Marca de Producto
            </h1>

            <!-- Navegación alfabética -->
            <div class="mb-8">
              <div
                class="flex gap-2 overflow-x-auto pb-2 md:flex-wrap md:overflow-x-visible md:pb-0"
              >
                @for (letter of alphabet; track letter) {
                <button
                  (click)="setSelectedLetter(letter)"
                  class="px-3 py-2 text-sm font-medium border rounded transition-colors whitespace-nowrap flex-shrink-0"
                  [class]="
                    selectedLetter() === letter
                      ? 'bg-allasRed text-white border-allasRed'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  "
                >
                  {{ letter }}
                </button>
                }
              </div>
            </div>
          </div>

          <!-- Sección de marcas destacadas - solo se muestra si no hay letra seleccionada -->
          @if (!selectedLetter()) {
          <div class="mb-12">
            <div class="flex items-center mb-6">
              <h2 class="text-2xl font-bold text-gray-800">
                Marcas Destacadas
              </h2>
              <div class="ml-4 h-0.5 bg-allasRed flex-1"></div>
            </div>

            <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              @for (brand of featuredBrands(); track brand.marca) {
              <a [href]="getBrandUrl(brand.marca)" class="group block">
                <div
                  class="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow duration-200 h-32 flex items-center justify-center"
                >
                  @if (brand.url_imagen_marca) {
                  <img
                    [src]="brand.url_imagen_marca"
                    [alt]="brand.marca"
                    class="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-200"
                  />
                  } @else {
                  <div class="text-center">
                    <h3 class="font-bold text-gray-800 text-sm">
                      {{ brand.marca }}
                    </h3>
                  </div>
                  }
                </div>
              </a>
              }
            </div>
          </div>
          }

          <!-- Lista de marcas por letra seleccionada -->
          @if (selectedLetter()) {
          <div>
            <h2 class="text-3xl font-bold text-gray-800 mb-6">
              {{ selectedLetter() }}
            </h2>

            @if (filteredBrands().length > 0) {
            <div
              class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            >
              @for (brand of filteredBrands(); track brand.marca) {
              <a [href]="getBrandUrl(brand.marca)" class="block">
                <div
                  class="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200"
                >
                  <div class="flex items-center space-x-3">
                    @if (brand.url_imagen_marca) {
                    <div class="w-12 h-12 flex-shrink-0">
                      <img
                        [src]="brand.url_imagen_marca"
                        [alt]="brand.marca"
                        class="w-full h-full object-contain"
                      />
                    </div>
                    } @else {
                    <div
                      class="w-12 h-12 bg-gray-100 rounded flex items-center justify-center flex-shrink-0"
                    >
                      <span class="text-gray-400 text-xs font-bold">{{
                        brand.marca.charAt(0)
                      }}</span>
                    </div>
                    }
                    <div class="flex-1 min-w-0">
                      <h3 class="font-semibold text-gray-800 text-sm truncate">
                        {{ brand.marca }}
                      </h3>
                      <p class="text-xs text-gray-500">
                        {{
                          brand.cantidad_productos_vendidos_2025.toLocaleString()
                        }}
                        productos vendidos
                      </p>
                    </div>
                  </div>
                </div>
              </a>
              }
            </div>
            } @else {
            <p class="text-gray-500 text-center py-8">
              No hay marcas disponibles para la letra "{{ selectedLetter() }}"
            </p>
            }
          </div>
          }

          <!-- Mostrar todas las marcas organizadas alfabéticamente cuando no hay letra seleccionada -->
          @if (!selectedLetter()) {
          <div class="space-y-8">
            @for (letter of alphabet; track letter) { @if
            (brandsByLetter()[letter] && brandsByLetter()[letter].length > 0) {
            <div>
              <h2
                class="text-2xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2"
              >
                {{ letter }}
              </h2>
              <div
                class="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-1 md:gap-3"
              >
                @for (brand of brandsByLetter()[letter]; track brand.marca) {
                <a
                  [href]="getBrandUrl(brand.marca)"
                  class="block text-gray-700 hover:text-allasRed hover:underline text-sm py-1 transition-colors"
                >
                  {{ brand.marca }}
                </a>
                }
              </div>
            </div>
            } }
          </div>
          }
        </div>
      </allas-margin-wrapper>
    </allas-main-layout>
  `,
})
export class ComprarPorMarcaProducto implements OnInit {
  private title = inject(Title);
  private meta = inject(Meta);

  selectedLetter = signal<string | null>(null);

  // Alfabeto para la navegación
  alphabet = [
    '0-9',
    'A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'G',
    'H',
    'I',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'P',
    'Q',
    'R',
    'S',
    'T',
    'U',
    'V',
    'W',
    'X',
    'Y',
    'Z',
  ];

  // Primeras 12 marcas para la sección destacada
  featuredBrands = computed(() =>
    marcasProductos.filter((brand) => brand.is_featured)
  );

  // Organizar marcas por letra
  brandsByLetter = computed(() => {
    const grouped: { [key: string]: Brand[] } = {};

    marcasProductos.forEach((brand) => {
      const firstChar = brand.marca.charAt(0).toUpperCase();
      let letter = firstChar;

      // Si es un número, agruparlo en "0-9"
      if (/[0-9]/.test(firstChar)) {
        letter = '0-9';
      }

      if (!grouped[letter]) {
        grouped[letter] = [];
      }
      grouped[letter].push(brand);
    });

    // Ordenar marcas dentro de cada letra
    Object.keys(grouped).forEach((letter) => {
      grouped[letter].sort((a, b) => a.marca.localeCompare(b.marca));
    });

    return grouped;
  });

  // Filtrar marcas según la letra seleccionada
  filteredBrands = computed(() => {
    if (!this.selectedLetter()) return [];
    return this.brandsByLetter()[this.selectedLetter()!] || [];
  });

  setSelectedLetter(letter: string) {
    this.selectedLetter.set(this.selectedLetter() === letter ? null : letter);
  }

  getBrandUrl(brandName: string): string {
    return '/productos/marca/' + toSlug(brandName);
  }

  ngOnInit() {
    this.setMetaTags();
  }

  private setMetaTags() {
    const pageTitle = 'Comprar por Marca de Producto | Allas Repuestos';
    const description =
      'Encuentra repuestos por marca con envío a domicilio. Amplia selección de marcas automotrices con garantía y calidad en Honduras.';
    const keywords =
      'repuestos por marca, Allas Repuestos, envío a domicilio, Honduras, marcas automotrices, garantía, calidad, instalación';
    const imageUrl = 'https://www.allasrepuestos.com/logos/isotipo.webp';

    // --- Título de la página ---
    this.title.setTitle(pageTitle);

    // --- Meta Descripción ---
    this.meta.updateTag({
      name: 'description',
      content: description,
    });

    // --- Palabras Clave ---
    this.meta.updateTag({
      name: 'keywords',
      content: keywords,
    });

    // --- Open Graph Tags ---
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:image', content: imageUrl });
    this.meta.updateTag({
      property: 'og:image:alt',
      content: 'Logo de Allas Repuestos',
    });

    // --- Twitter Tags ---
    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: imageUrl });

    // --- Icono del sitio (favicon / logo principal) ---
    this.meta.updateTag({
      name: 'icon',
      content: imageUrl,
    });
  }
}
